import { useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { DateTime as DT } from 'luxon';
import { useTranslation } from 'react-i18next';
import * as apiObject from 'api/apiObjects';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import InputPriceRate from 'components/others/InputPriceRate';
import SolarIcon from 'components/icons/SolarIcon';
import spinnerTitle from 'components/others/spinnerTitle';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import { useObjectCard } from '../../context/ObjectCardProvider';
import ExpensesTable from '../ExpensesTable';
import type { ExpendTeamInfoData, IncomeExpenseRow } from '../model';

interface TabExpensesProps {
    objectId: number;
    expendId: number;
    expendTeam: ExpendTeamInfoData;
    readOnly: boolean;
    onSaved: () => Promise<void> | void;
}

const todayIso = () => DT.now().toISODate() || '';

const isSyncOn = (expendTeam: ExpendTeamInfoData): boolean =>
    Number(expendTeam.expend?.income_sync_cost ?? expendTeam.expend?.incomeSyncCost ?? 0) === 1;

const TabExpenses = ({ objectId, expendId, expendTeam, readOnly, onSaved }: TabExpensesProps) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const { fetchObject } = useObjectCard();
    const [amount, setAmount] = useState(0);
    const [dateValue, setDateValue] = useState(todayIso);
    const [comment, setComment] = useState('');
    const [busy, setBusy] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const syncOn = isSyncOn(expendTeam);
    const rows = expendTeam.expenses || [];

    const toastError = (errCode?: string) => {
        if (errCode === 'OBJECT_LOCKED') showToast(t('expendTeamExpenses.errLocked'), 'danger');
        else if (errCode === 'CANCELLED') showToast(t('expendTeamExpenses.errCancelled'), 'danger');
        else if (errCode === 'AMOUNT') showToast(t('expendTeamExpenses.errAmount'), 'danger');
        else showToast(t('common:messError'), 'danger');
    };

    const refresh = async (costUpdated: boolean) => {
        await onSaved();
        if (costUpdated) await fetchObject();
    };

    const handleSync = async (enabled: boolean) => {
        if (readOnly || busy || deletingId) return;
        setBusy(true);
        const res = await apiObject.setExpendTeamExpenseSync(objectId, expendId, enabled);
        if (!res?.stat) {
            setBusy(false);
            toastError(res?.errCode);
            return;
        }
        await refresh(Boolean(res.costUpdated));
        setBusy(false);
        showToast(
            enabled ? t('expendTeamExpenses.messSyncOn') : t('expendTeamExpenses.messSyncOff'),
            'success',
        );
    };

    const handleAdd = async () => {
        if (readOnly || busy || deletingId) return;
        if (!(Number(amount) > 0)) {
            showToast(t('expendTeamExpenses.errAmount'), 'danger');
            return;
        }
        const dt = dateValue ? DT.fromISO(dateValue).toMillis() : 0;
        if (!dt) {
            showToast(t('common:messError'), 'danger');
            return;
        }
        setBusy(true);
        const res = await apiObject.addExpendTeamExpense(objectId, expendId, {
            amount: Number(amount),
            dt,
            comment,
        });
        if (!res?.stat) {
            setBusy(false);
            toastError(res?.errCode);
            return;
        }
        setAmount(0);
        setComment('');
        await refresh(Boolean(res.costUpdated));
        setBusy(false);
        showToast(t('expendTeamExpenses.messAdded'), 'success');
    };

    const handleDelete = (row: IncomeExpenseRow) => {
        if (readOnly || busy || deletingId) return;
        const buttons = [...getButtonsNoYesDanger(t)];
        buttons[1].handleClick = async () => {
            showDlg(false);
            setDeletingId(row.ID);
            const res = await apiObject.cancelExpendTeamExpense(objectId, expendId, row.ID);
            if (!res?.stat) {
                setDeletingId(null);
                toastError(res?.errCode);
                return;
            }
            await refresh(Boolean(res.costUpdated));
            setDeletingId(null);
            showToast(t('expendTeamExpenses.messDeleted'), 'success');
        };
        showDlg({
            type: 'confirm',
            buttons,
            text: t('expendTeamExpenses.confirmDelete'),
        });
    };

    return (
        <div className="off-create-team-content px-3 pt-3 pb-3">
            <div className="form-check form-switch mb-3">
                <input
                    type="checkbox"
                    className="form-check-input"
                    role="switch"
                    id={`expend-sync-cost-${expendId}`}
                    checked={syncOn}
                    disabled={readOnly || busy || deletingId != null}
                    onChange={(e) => void handleSync(e.target.checked)}
                />
                <label className="form-check-label" htmlFor={`expend-sync-cost-${expendId}`}>
                    <span className="d-block">{t('expendTeamExpenses.syncCost')}</span>
                    <span className="d-block text-muted fs-12">{t('expendTeamExpenses.syncCostHint')}</span>
                </label>
            </div>

            {!readOnly ? (
                <Form
                    className="mb-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void handleAdd();
                    }}
                >
                    <Row className="g-2">
                        <Col xs={12} md={6}>
                            <Form.Group>
                                <label className="form-label text-muted">
                                    {t('expendTeamExpenses.labelAmount')}
                                </label>
                                <InputPriceRate
                                    value={amount || 0}
                                    allowNegative={false}
                                    disabled={busy || deletingId != null}
                                    onValueChange={(val) => setAmount(Number(val?.floatValue) || 0)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12} md={6}>
                            <Form.Group>
                                <label className="form-label text-muted">
                                    {t('expendTeamExpenses.labelDate')}
                                </label>
                                <AppDatePicker
                                    disabled={busy || deletingId != null}
                                    value={dateValue}
                                    onChange={setDateValue}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <Form.Group>
                                <label className="form-label text-muted">
                                    {t('expendTeamExpenses.labelComment')}
                                </label>
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    maxLength={2000}
                                    disabled={busy || deletingId != null}
                                    value={comment}
                                    onChange={(e) => setComment(e.currentTarget.value)}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                        <Button
                            type="submit"
                            variant="primary"
                            size="sm"
                            className="mt-3"
                            disabled={busy || deletingId != null}
                        >
                            {spinnerTitle(
                                <>
                                    <SolarIcon name="plus" className="me-1 fs-14" />
                                    {t('expendTeamExpenses.add')}
                                </>,
                                busy,
                                t('expendTeamExpenses.add'),
                            )}
                        </Button>
                    </div>
                </Form>
            ) : null}

            <ExpensesTable
                rows={rows}
                readOnly={readOnly}
                deletingId={deletingId}
                onDelete={handleDelete}
            />
        </div>
    );
};

export default TabExpenses;
